import { Formik } from 'formik';
import Button from '../../../../components/form-control/Button';
import Input from '../../../../components/form-control/Input';
import * as Yup from 'yup';
import SelectDropdown from '../../../../components/form-control/SelectDropdown';
import DatePickerInput from '../../../../components/form-control/DatePickerInput';
import { ICreateSubscriptionPlan, IEnum, } from '../../../../resources/interfaces';
import Checkbox from '../../../../components/form-control/Checkbox';
import TextArea from '../../../../components/form-control/TextArea';
import { createSubscriptionPlan, updateSubscriptionPlan } from '../../../../services/billing';
import { useState } from 'react';
import { getEnumsFromState } from '../../../../states/slices/resourceSlice';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useRoute from '../../../../hooks/routesHook';
import BackButton from '../../../../components/BackButton';
import { useQuery } from '@tanstack/react-query';
import useBilling from '../../../../hooks/billingHook';
import { showToast } from '../../../../utils/miscFunctions';

const CreateSubscriptionPlan = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { fetchSubscriptionPlanById } = useBilling()
    const enums = useSelector(getEnumsFromState);
    const navigate = useNavigate()
    const { getDashboardBase } = useRoute()
    const [params] = useSearchParams()
    const planId = params.get('id') || ''
    const backRoute = getDashboardBase() + '/settings?tab=billing'

    const { data: subscriptionPlan } = useQuery({
        queryKey: ['subscription-plan', planId],
        queryFn: () => fetchSubscriptionPlanById(planId),
        enabled: !!planId || planId !== '' || planId !== null || planId !== undefined,
    })

    const getDisplayOrderFromPlanTier = (planTier: string) => {
        switch (planTier) {
            case 'Free':
                return 0;
            case 'Basic':
                return 1;
            case 'Standard':
                return 2;
            case 'Premium':
                return 3;
            case 'Professional':
                return 4;
            case 'Enterprise':
                return 5;
            case 'Custom':
                return 6;
            default:
                return 0;
        }
    }

    const initialValues: ICreateSubscriptionPlan = {
        availableFrom: subscriptionPlan?.availableFrom || null,
        availableUntil: subscriptionPlan?.availableUntil || null,
        basePrice: subscriptionPlan?.basePrice || null,
        billingCycle: subscriptionPlan?.billingCycle || '',
        currency: subscriptionPlan?.currency || '',
        customFeatures: subscriptionPlan?.customFeatures || '',
        description: subscriptionPlan?.description || '',
        discountPercentage: subscriptionPlan?.discountPercentage || null,
        displayOrder: subscriptionPlan?.displayOrder || null,
        has24x7Support: subscriptionPlan?.has24x7Support || false,
        hasAccountManager: subscriptionPlan?.hasAccountManager || false,
        hasAdvancedReports: subscriptionPlan?.hasAdvancedReports || false,
        hasAnalytics: subscriptionPlan?.hasAnalytics || false,
        hasApiAccess: subscriptionPlan?.hasApiAccess || false,
        hasCustomIntegrations: subscriptionPlan?.hasCustomIntegrations || false,
        hasPrioritySupport: subscriptionPlan?.hasPrioritySupport || false,
        hasTrial: subscriptionPlan?.hasTrial || false,
        hasWhiteLabel: subscriptionPlan?.hasWhiteLabel || false,
        internalNotes: subscriptionPlan?.internalNotes || '',
        isActive: subscriptionPlan?.active || false,
        isFeatured: subscriptionPlan?.featured || false,
        isPublic: subscriptionPlan?.public || false,
        maxApiCalls: subscriptionPlan?.maxApiCalls || null,
        maxBranches: subscriptionPlan?.maxBranches || null,
        maxMotorPolicies: subscriptionPlan?.maxMotorPolicies || null,
        maxNonMotorPolicies: subscriptionPlan?.maxNonMotorPolicies || null,
        maxStorageGb: subscriptionPlan?.maxStorageGb || null,
        maxUsers: subscriptionPlan?.maxUsers || null,
        name: subscriptionPlan?.name || '',
        planCode: subscriptionPlan?.planCode || '',
        planStatus: subscriptionPlan?.planStatus || '',
        planTier: subscriptionPlan?.planTier || '',
        productIds: subscriptionPlan?.products || [],
        setupFee: subscriptionPlan?.setupFee || null,
        supportResponseHours: subscriptionPlan?.supportResponseHours || null,
        tagline: subscriptionPlan?.tagline || '',
        terms: subscriptionPlan?.terms || '',
        companyType: subscriptionPlan?.companyType || '',
        trialDays: subscriptionPlan?.trialDays || null,
        trialRequiresCard: subscriptionPlan?.trialRequiresCard || false,
        unlimitedPolicies: subscriptionPlan?.unlimitedPolicies || false,
    };


    return (
        <div className="bg-white dark:bg-darkBg dark:text-darkBg-mainText dark:border dark:border-darkBg-border rounded-xl mt-4">
            <div className="flex gap-5 items-center px-6 py-4">
                <BackButton onClick={() => navigate(backRoute)} />
                <h2 className="text-2xl font-bold dark:text-darkBg-mainText">Create Subscription Plan</h2>
            </div>

            <div className="py-6 px-3 md:px-6  w-full">
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validateOnChange={true}
                    validateOnBlur={true}
                    validationSchema={Yup.object().shape({
                        name: Yup.string().required('Name is required.'),
                        availableUntil: Yup.string().nullable().required('Available Until is required.'),
                        availableFrom: Yup.string().nullable().required('Available From is required.'),
                        basePrice: Yup.number().nullable().required('Base Price is required.'),
                        billingCycle: Yup.string().required('Billing Cycle is required.'),
                        currency: Yup.string().required('Currency is required.'),
                        description: Yup.string().required('Description is required.'),
                        planTier: Yup.string().required('Plan Tier is required.'),
                    })}
                    onSubmit={async (values: ICreateSubscriptionPlan, { resetForm }) => {
                        setIsSubmitting(true);
                        const createPayload: ICreateSubscriptionPlan= {
                            currency: values.currency,
                            planStatus: values.isActive ? 'ACTIVE' : 'INACTIVE',
                            planTier: values.planTier,
                            availableFrom: values.availableFrom ? new Date(values.availableFrom).toISOString() : null,
                            availableUntil: values.availableUntil ? new Date(values.availableUntil).toISOString() : null,
                            basePrice: values.basePrice,
                            billingCycle: values.billingCycle,
                            customFeatures: values.customFeatures,
                            description: values.description,
                            discountPercentage: values.discountPercentage,
                            displayOrder: getDisplayOrderFromPlanTier(values.planTier),
                            has24x7Support: values.has24x7Support,
                            hasAccountManager: values.hasAccountManager,
                            hasAdvancedReports: values.hasAdvancedReports,
                            hasAnalytics: values.hasAnalytics,
                            hasApiAccess: values.hasApiAccess,
                            hasCustomIntegrations: values.hasCustomIntegrations,
                            hasPrioritySupport: values.hasPrioritySupport,
                            hasTrial: values.hasTrial,
                            hasWhiteLabel: values.hasWhiteLabel,
                            internalNotes: values.internalNotes,
                            isActive: values.isActive,
                            isFeatured: values.isFeatured,
                            isPublic: values.isPublic,
                            maxApiCalls: values.maxApiCalls,
                            maxBranches: values.maxBranches,
                            maxMotorPolicies: values.maxMotorPolicies,
                            maxNonMotorPolicies: values.maxNonMotorPolicies,
                            maxStorageGb: values.maxStorageGb,
                            maxUsers: values.maxUsers,
                            name: values.name,
                            planCode: values.planCode,
                            productIds: values.productIds || [],
                            setupFee: values.setupFee,
                            supportResponseHours: values.supportResponseHours,
                            tagline: values.tagline,
                            terms: values.terms,
                            companyType: values.companyType,
                            trialDays: values.trialDays,
                            trialRequiresCard: values.trialRequiresCard,
                            unlimitedPolicies: values.unlimitedPolicies
                        };

                        const updatePayload: ICreateSubscriptionPlan= {
                            currency: values.currency,
                            planStatus: values.isActive ? 'ACTIVE' : 'INACTIVE',
                            planTier: values.planTier,
                            availableFrom: values.availableFrom ? new Date(values.availableFrom).toISOString() : null,
                            availableUntil: values.availableUntil ? new Date(values.availableUntil).toISOString() : null,
                            basePrice: values.basePrice,
                            billingCycle: values.billingCycle,
                            customFeatures: values.customFeatures,
                            description: values.description,
                            discountPercentage: values.discountPercentage,
                            displayOrder: getDisplayOrderFromPlanTier(values.planTier),
                            has24x7Support: values.has24x7Support,
                            hasAccountManager: values.hasAccountManager,
                            hasAdvancedReports: values.hasAdvancedReports,
                            hasAnalytics: values.hasAnalytics,
                            hasApiAccess: values.hasApiAccess,
                            hasCustomIntegrations: values.hasCustomIntegrations,
                            hasPrioritySupport: values.hasPrioritySupport,
                            hasTrial: values.hasTrial,
                            hasWhiteLabel: values.hasWhiteLabel,
                            internalNotes: values.internalNotes,
                            isActive: values.isActive,
                            isFeatured: values.isFeatured,
                            isPublic: values.isPublic,
                            maxApiCalls: values.maxApiCalls,
                            maxBranches: values.maxBranches,
                            maxMotorPolicies: values.maxMotorPolicies,
                            maxNonMotorPolicies: values.maxNonMotorPolicies,
                            maxStorageGb: values.maxStorageGb,
                            maxUsers: values.maxUsers,
                            name: values.name,
                            planCode: values.planCode,
                            productIds: values.productIds || [],
                            setupFee: values.setupFee,
                            supportResponseHours: values.supportResponseHours,
                            tagline: values.tagline,
                            terms: values.terms,
                            companyType: values.companyType,
                            trialDays: values.trialDays,
                            trialRequiresCard: values.trialRequiresCard,
                            unlimitedPolicies: values.unlimitedPolicies
                        };

                        try {
                            setIsSubmitting(true);
                            const response: any = planId ? await updateSubscriptionPlan(planId, updatePayload) : await createSubscriptionPlan(createPayload);
                            if (response) {
                                showToast.success(response?.message || 'Subscription plan created successfully');
                                navigate(backRoute)
                            }
                        } catch (error: any) {
                            console.log(error);
                            showToast.error(error?.description || 'Failed to create subscription plan');
                        } finally {
                            setIsSubmitting(false);
                        }


                    }}>
                    {({ values, errors, isSubmitting, handleSubmit, handleChange, handleBlur, setFieldValue, touched, isValid }) => (
                        <form className="border border-gray-400 dark:border-b-darkBg-border p-6 rounded-xl space-y-6" onSubmit={handleSubmit}>

                            {/* Basic Information Section */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium dark:text-darkBg-mainText">Basic Information</h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Plan Name"
                                        placeholder="Enter plan name"
                                        required
                                        error={touched.name ? errors.name : undefined}
                                        helperText={touched.name ? errors.name : undefined}
                                    />
                                    <Input
                                        name="planCode"
                                        value={values.planCode}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Plan Code"
                                        placeholder="Enter plan code"
                                        required
                                        error={touched.planCode ? errors.planCode : undefined}
                                        helperText={touched.planCode ? errors.planCode : undefined}
                                    />
                                    <Input
                                        name="tagline"
                                        value={values.tagline}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Tagline"
                                        placeholder="Enter plan tagline"
                                        required
                                        error={touched.tagline ? errors.tagline : undefined}
                                        helperText={touched.tagline ? errors.tagline : undefined}
                                    />

                                    <SelectDropdown
                                        name="planTier"
                                        listSelectedValue={values.planTier}
                                        list={enums?.planTiers?.map((tier: IEnum) => ({
                                            name: tier.title,
                                            value: tier.key
                                        }))}
                                        onChange={(value: string) => { setFieldValue('planTier', value); }} onBlur={handleBlur}
                                        label="Plan Tier"
                                        placeholder="Enter plan tier"
                                        required
                                        error={touched.planTier ? errors.planTier : undefined}
                                        helperText={touched.planTier ? errors.planTier : undefined}
                                    />

                                    <DatePickerInput
                                        id="availableFrom"
                                        selected={values.availableFrom ? new Date(values.availableFrom) : null}
                                        onChange={(date) => setFieldValue('availableFrom', date)}
                                        label="Available From"
                                        placeholder="Select start date"
                                        error={touched.availableFrom ? errors.availableFrom : undefined}
                                        helperText={touched.availableFrom ? errors.availableFrom : undefined}
                                        dateFormat="dd/MM/yyyy"
                                        required
                                    />

                                    <DatePickerInput
                                        id="availableUntil"
                                        selected={values.availableUntil ? new Date(values.availableUntil) : null}
                                        onChange={(date) => setFieldValue('availableUntil', date)}
                                        label="Available Until"
                                        placeholder="Select end date "
                                        error={touched.availableUntil ? errors.availableUntil : undefined}
                                        helperText={touched.availableUntil ? errors.availableUntil : undefined}
                                        dateFormat="dd/MM/yyyy"
                                        required
                                    />

                                    <div className="md:col-span-2">
                                        <TextArea
                                            name="description"
                                            value={values.description}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Description"
                                            placeholder="Enter plan description"
                                            rows={3}
                                            required
                                            error={touched.description ? errors.description : undefined}
                                            helperText={touched.description ? errors.description : undefined}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Pricing Section */}
                            <div className="space-y-4 pt-4">
                                <h3 className="text-lg font-medium dark:text-darkBg-mainText">Pricing</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Input
                                        type="number"
                                        name="basePrice"
                                        value={values.basePrice}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Base Price"
                                        placeholder="Enter base price"
                                        min={0}
                                        required
                                        error={touched.basePrice ? errors.basePrice : undefined}
                                        helperText={touched.basePrice ? errors.basePrice : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="setupFee"
                                        value={values.setupFee}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Setup Fee"
                                        placeholder="Enter setup fee"
                                        min={0}
                                        error={touched.setupFee ? errors.setupFee : undefined}
                                        helperText={touched.setupFee ? errors.setupFee : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="discountPercentage"
                                        value={values.discountPercentage}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Discount Percentage"
                                        placeholder="Enter discount percentage"
                                        min={0}
                                        max={100}
                                    />

                                    <SelectDropdown
                                        name="billingCycle"
                                        listSelectedValue={values.billingCycle}
                                        list={enums?.billingCycles?.map((cycle: IEnum) => ({
                                            name: cycle.title,
                                            value: cycle.key
                                        }))}
                                        onChange={(value: string) => setFieldValue('billingCycle', value)}
                                        onBlur={handleBlur}
                                        label="Billing Cycle"
                                        placeholder="e.g. Monthly, Quarterly, Annually"
                                        error={touched.billingCycle ? errors.billingCycle : undefined}
                                        helperText={touched.billingCycle ? errors.billingCycle : undefined}
                                    />

                                    <SelectDropdown
                                        name="currency"
                                        listSelectedValue={values.currency}
                                        list={enums?.currencyCodes?.map((currency: IEnum) => ({
                                            name: currency.title,
                                            value: currency.key
                                        }))}
                                        onChange={(value: string) => setFieldValue('currency', value)}
                                        onBlur={handleBlur}
                                        label="Currency"
                                        placeholder="e.g. USD, GHS"
                                        error={touched.currency ? errors.currency : undefined}
                                        helperText={touched.currency ? errors.currency : undefined}
                                    />
                                </div>
                            </div>

                            {/* Feature Toggles */}
                            <div className="space-y-4 pt-4">
                                <h3 className="text-lg font-medium dark:text-darkBg-mainText">Feature Toggles</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <Checkbox
                                        name="has24x7Support"
                                        checked={values.has24x7Support}
                                        onChange={handleChange}
                                        label="24/7 Support"
                                    />
                                    <Checkbox
                                        name="hasAccountManager"
                                        checked={values.hasAccountManager}
                                        onChange={handleChange}
                                        label="Dedicated Account Manager"
                                    />
                                    <Checkbox
                                        name="hasAdvancedReports"
                                        checked={values.hasAdvancedReports}
                                        onChange={handleChange}
                                        label="Advanced Reports"
                                    />
                                    <Checkbox
                                        name="hasAnalytics"
                                        checked={values.hasAnalytics}
                                        onChange={handleChange}
                                        label="Analytics Dashboard"
                                    />
                                    <Checkbox
                                        name="hasApiAccess"
                                        checked={values.hasApiAccess}
                                        onChange={handleChange}
                                        label="API Access"
                                    />
                                    <Checkbox
                                        name="hasCustomIntegrations"
                                        checked={values.hasCustomIntegrations}
                                        onChange={handleChange}
                                        label="Custom Integrations"
                                    />
                                    <Checkbox
                                        name="hasPrioritySupport"
                                        checked={values.hasPrioritySupport}
                                        onChange={handleChange}
                                        label="Priority Support"
                                    />
                                    <Checkbox
                                        name="hasTrial"
                                        checked={values.hasTrial}
                                        onChange={handleChange}
                                        label="Free Trial Available"
                                    />
                                    <Checkbox
                                        name="hasWhiteLabel"
                                        checked={values.hasWhiteLabel}
                                        onChange={handleChange}
                                        label="White Label"
                                    />

                                    <Checkbox
                                        name="isPublic"
                                        checked={values.isPublic}
                                        onChange={handleChange}
                                        label="Publicly Visible"
                                    />
                                    <Checkbox
                                        name="trialRequiresCard"
                                        checked={values.trialRequiresCard}
                                        onChange={handleChange}
                                        label="Trial Requires Credit Card"
                                    />
                                    <Checkbox
                                        name="unlimitedPolicies"
                                        checked={values.unlimitedPolicies}
                                        onChange={handleChange}
                                        label="Unlimited Policies"
                                    />
                                </div>
                            </div>

                            {/* Limits Section */}
                            <div className="space-y-4 pt-4">
                                <h3 className="text-lg font-medium dark:text-darkBg-mainText">Limits</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <SelectDropdown
                                        name="companyType"
                                        listSelectedValue={values.companyType}
                                        onChange={(type: string) => setFieldValue("companyType", type)}
                                        onBlur={handleBlur}
                                        label="Company Type"
                                        placeholder="Select company type"
                                        list={enums?.companyTypes?.map((item: IEnum) => ({
                                            name: item.title,
                                            value: item.key,
                                        }))}
                                        error={touched.companyType ? errors.companyType : undefined}
                                        helperText={touched.companyType ? errors.companyType : undefined}
                                    />

                                    <Input
                                        type="number"
                                        name="maxUsers"
                                        value={values.maxUsers}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Max Users"
                                        placeholder="0"
                                        min={0}
                                        error={touched.maxUsers ? errors.maxUsers : undefined}
                                        helperText={touched.maxUsers ? errors.maxUsers : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="maxBranches"
                                        value={values.maxBranches}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Max Branches"
                                        placeholder="0"
                                        min={0}
                                        error={touched.maxBranches ? errors.maxBranches : undefined}
                                        helperText={touched.maxBranches ? errors.maxBranches : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="maxStorageGb"
                                        value={values.maxStorageGb}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Max Storage (GB)"
                                        placeholder="0"
                                        min={0}
                                        error={touched.maxStorageGb ? errors.maxStorageGb : undefined}
                                        helperText={touched.maxStorageGb ? errors.maxStorageGb : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="maxApiCalls"
                                        value={values.maxApiCalls}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Max API Calls"
                                        placeholder="0"
                                        min={0}
                                        error={touched.maxApiCalls ? errors.maxApiCalls : undefined}
                                        helperText={touched.maxApiCalls ? errors.maxApiCalls : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="maxMotorPolicies"
                                        value={values.maxMotorPolicies}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Max Motor Policies"
                                        placeholder="0"
                                        min={0}
                                        error={touched.maxMotorPolicies ? errors.maxMotorPolicies : undefined}
                                        helperText={touched.maxMotorPolicies ? errors.maxMotorPolicies : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="maxNonMotorPolicies"
                                        value={values.maxNonMotorPolicies}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Max Non-Motor Policies"
                                        placeholder="0"
                                        min={0}
                                        error={touched.maxNonMotorPolicies ? errors.maxNonMotorPolicies : undefined}
                                        helperText={touched.maxNonMotorPolicies ? errors.maxNonMotorPolicies : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="supportResponseHours"
                                        value={values.supportResponseHours}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Support Response Time (Hours)"
                                        placeholder="24"
                                        min={0}
                                        error={touched.supportResponseHours ? errors.supportResponseHours : undefined}
                                        helperText={touched.supportResponseHours ? errors.supportResponseHours : undefined}
                                    />
                                    <Input
                                        type="number"
                                        name="trialDays"
                                        value={values.trialDays}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Trial Days"
                                        placeholder="0"
                                        min={0}
                                        error={touched.trialDays ? errors.trialDays : undefined}
                                        helperText={touched.trialDays ? errors.trialDays : undefined}
                                    />
                                </div>
                            </div>

                            {/* Additional Information */}
                            <div className="space-y-4 pt-4">
                                <h3 className="text-lg font-medium dark:text-darkBg-mainText">Additional Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                    <div className="md:col-span-2">
                                        <TextArea
                                            name="customFeatures"
                                            value={values.customFeatures}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Custom Features (comma-separated)"
                                            placeholder="Feature 1, Feature 2, Feature 3"
                                            rows={2}
                                            error={touched.customFeatures ? errors.customFeatures : undefined}
                                            helperText={touched.customFeatures ? errors.customFeatures : undefined}
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <TextArea
                                            name="terms"
                                            value={values.terms}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Terms & Conditions"
                                            placeholder="Enter terms and conditions"
                                            rows={3}
                                            error={touched.terms ? errors.terms : undefined}
                                            helperText={touched.terms ? errors.terms : undefined}
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <TextArea
                                            name="internalNotes"
                                            value={values.internalNotes}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Internal Notes"
                                            placeholder="Internal notes (not visible to customers)"
                                            rows={2}
                                            error={touched.internalNotes ? errors.internalNotes : undefined}
                                            helperText={touched.internalNotes ? errors.internalNotes : undefined}
                                        />
                                    </div>

                                </div>
                            </div>




                            <div className="w-full flex justify-between items-end gap-4 mt-6 !mb-4">
                                <div className="flex justify-end items-center gap-6 my-4">
                                    <Checkbox
                                        name="isActive"
                                        checked={values.isActive}
                                        onChange={handleChange}
                                        label="Do you want to activate this plan?"
                                    />

                                    <Checkbox
                                        name="isFeatured"
                                        checked={values.isFeatured}
                                        onChange={handleChange}
                                        label="Do you want to feature this plan?"
                                    />
                                </div>
                                <Button
                                    label="Submit"
                                    type='submit'
                                    bgColor='primary'
                                    size='lg'
                                    disabled={isSubmitting || !isValid}
                                />
                            </div>

                        </form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default CreateSubscriptionPlan;
